/**
 * Topi Elements Integration
 * Handles initialization and data management for topi rental elements
 */

const initTopi = () => {
  const topi = (window.topi = new TopiElements({
    locale: "de",
    widgetId: "e7a85111-2a7d-4d0f-8efa-655efd9c84df",
  }));

  // Store current PDP item for checkout
  let currentPdpItem = null;

  // Store eligible cart items for checkout
  let eligibleCartItems = [];
  let ineligibleCartItems = [];
  let isCartMode = false;

  /**
   * Generate a clean order reference number
   * Format: FK-YYYYMMDD-XXXXX (e.g., FK-20240129-A7B3C)
   */
  function generateOrderReference() {
    const now = new Date();
    const date = now.toISOString().slice(0, 10).replace(/-/g, '');
    const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789';
    let id = '';
    for (let i = 0; i < 5; i++) {
      id += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    return `FK-${date}-${id}`;
  }

  /**
   * Set the PDP item for topi Elements
   * Called from product pages to display rental pricing for a single product
   * @param {Object} productData - Product data from Shopify
   */
  window.setTopiPdpItem = (productData) => {
    if (!productData || !productData.sku) {
      return;
    }

    const grossPrice = productData.price || 0;
    const taxRate = 1900; // 19% German VAT
    const netPrice = Math.round(grossPrice / 1.19);

    const pdpItem = {
      quantity: productData.quantity || 1,
      sellerProductReference: {
        reference: productData.sku,
        source: "shopify",
      },
      price: {
        currency: "EUR",
        gross: grossPrice,
        net: netPrice,
        taxRate: taxRate,
      },
      title: productData.title || "",
      image: productData.image || "",
    };

    // Store for checkout handler
    currentPdpItem = pdpItem;
    topi.pdpItem = pdpItem;
  };

  /**
   * Handle PDP checkout button click
   * Opens the modal to collect shipping address
   */
  window.handleTopiPdpCheckout = () => {
    if (!currentPdpItem) {
      console.error("Topi: No PDP item set");
      return;
    }
    window.openTopiModal();
  };

  /**
   * Open the topi checkout modal
   * @param {boolean} cartMode - If true, shows cart items; if false, shows PDP item
   */
  window.openTopiModal = (cartMode = false) => {
    const modal = document.getElementById("topi-checkout-modal");
    if (!modal) {
      console.error("Topi modal not found");
      return;
    }

    isCartMode = cartMode;

    // Populate product list in modal
    const productList = document.getElementById("topi-modal-product-list");
    const ineligibleMessage = document.getElementById("topi-modal-ineligible-message");

    if (productList) {
      if (cartMode && eligibleCartItems.length > 0) {
        // Cart mode: show all eligible items
        let html = '';
        eligibleCartItems.forEach(item => {
          const pricePerMonth = (item.price.gross / 100 / 36).toFixed(2).replace(".", ",");
          html += `
            <div class="topi-modal__product-item">
              <img src="${item.image || ""}" alt="${item.title}" class="topi-modal__product-image" onerror="this.style.display='none'">
              <div class="topi-modal__product-info">
                <span class="topi-modal__product-title">${item.quantity}x ${item.title}</span>
                <span class="topi-modal__product-price">ab ${pricePerMonth} €/Mo.</span>
              </div>
            </div>
          `;
        });
        productList.innerHTML = html;

        // Show ineligible message if some items couldn't be rented
        if (ineligibleMessage && ineligibleCartItems.length > 0) {
          const ineligibleNames = ineligibleCartItems.map(item => item.title).join(", ");
          ineligibleMessage.innerHTML = `<strong>Hinweis:</strong> Folgende Produkte sind nicht mietbar und wurden entfernt: ${ineligibleNames}`;
          ineligibleMessage.style.display = "block";
        } else if (ineligibleMessage) {
          ineligibleMessage.style.display = "none";
        }
      } else if (currentPdpItem) {
        // PDP mode: show single item
        const pricePerMonth = (currentPdpItem.price.gross / 100 / 36).toFixed(2).replace(".", ",");
        productList.innerHTML = `
          <div class="topi-modal__product-item">
            <img src="${currentPdpItem.image || ""}" alt="${currentPdpItem.title}" class="topi-modal__product-image" onerror="this.style.display='none'">
            <div class="topi-modal__product-info">
              <span class="topi-modal__product-title">${currentPdpItem.quantity}x ${currentPdpItem.title}</span>
              <span class="topi-modal__product-price">ab ${pricePerMonth} €/Mo.</span>
            </div>
          </div>
        `;
        if (ineligibleMessage) {
          ineligibleMessage.style.display = "none";
        }
      }
    }

    modal.style.display = "flex";
    document.body.style.overflow = "hidden";
  };

  /**
   * Close the topi checkout modal
   */
  window.closeTopiModal = () => {
    const modal = document.getElementById("topi-checkout-modal");
    if (modal) {
      modal.style.display = "none";
      document.body.style.overflow = "";
    }
  };

  /**
   * Submit the topi checkout form
   */
  window.submitTopiCheckoutForm = async (event) => {
    event.preventDefault();

    const form = document.getElementById("topi-checkout-form");
    const submitBtn = document.getElementById("topi-submit-btn");
    const submitText = submitBtn.querySelector(".topi-modal__submit-text");
    const submitLoading = submitBtn.querySelector(".topi-modal__submit-loading");

    // Clear previous errors
    form.querySelectorAll(".topi-modal__field").forEach((field) => {
      field.classList.remove("has-error");
    });

    // Get form values
    const name = document.getElementById("topi-name").value.trim();
    const email = document.getElementById("topi-email").value.trim();
    const street = document.getElementById("topi-street").value.trim();
    const country = document.getElementById("topi-country").value;
    const city = document.getElementById("topi-city").value.trim();
    const postal = document.getElementById("topi-postal").value.trim();

    // Validate
    let hasError = false;
    if (!name) {
      document.getElementById("topi-name").closest(".topi-modal__field").classList.add("has-error");
      hasError = true;
    }
    if (!email || !email.includes("@")) {
      document.getElementById("topi-email").closest(".topi-modal__field").classList.add("has-error");
      hasError = true;
    }
    if (!street) {
      document.getElementById("topi-street").closest(".topi-modal__field").classList.add("has-error");
      hasError = true;
    }
    if (!city) {
      document.getElementById("topi-city").closest(".topi-modal__field").classList.add("has-error");
      hasError = true;
    }
    if (!postal) {
      document.getElementById("topi-postal").closest(".topi-modal__field").classList.add("has-error");
      hasError = true;
    }

    if (hasError) {
      return false;
    }

    // Show loading state
    submitBtn.disabled = true;
    submitText.style.display = "none";
    submitLoading.style.display = "inline";

    try {
      const isLocalhost = window.location.hostname === "localhost" || window.location.hostname === "127.0.0.1";
      const urlParams = new URLSearchParams(window.location.search);
      const forceLocal = urlParams.get("topi_local") === "true" || localStorage.getItem("topi_local") === "true";
      const TOPI_API_PROXY_URL = (isLocalhost || forceLocal) ? "http://localhost:3000" : "/apps/topi/create-offer";

      // Build lines based on cart mode or PDP mode
      let lines = [];
      if (isCartMode && eligibleCartItems.length > 0) {
        lines = eligibleCartItems.map(item => ({
          price: {
            currency: item.price.currency,
            gross: item.price.gross,
            net: item.price.net,
            tax_rate: item.price.taxRate,
          },
          quantity: item.quantity,
          seller_product_reference: {
            reference: item.sellerProductReference.reference,
            source: item.sellerProductReference.source,
          },
          title: item.title,
        }));
      } else if (currentPdpItem) {
        lines = [{
          price: {
            currency: currentPdpItem.price.currency,
            gross: currentPdpItem.price.gross,
            net: currentPdpItem.price.net,
            tax_rate: currentPdpItem.price.taxRate,
          },
          quantity: currentPdpItem.quantity,
          seller_product_reference: {
            reference: currentPdpItem.sellerProductReference.reference,
            source: currentPdpItem.sellerProductReference.source,
          },
          title: currentPdpItem.title,
        }];
      }

      if (lines.length === 0) {
        throw new Error("Keine mietbaren Produkte gefunden");
      }

      const offerPayload = {
        lines: lines,
        customer: {
          email: email,
          full_name: name,
        },
        shipping_address: {
          recipient_name: name,
          line1: street,
          city: city,
          postal_code: postal,
          country_code: country,
        },
        sales_channel: "ecommerce",
        seller_offer_reference: generateOrderReference(),
        success_redirect: window.location.origin,
        exit_redirect: window.location.href,
      };

      const response = await fetch(TOPI_API_PROXY_URL, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        body: JSON.stringify(offerPayload),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || `Request failed: ${response.status}`);
      }

      const offerData = await response.json();

      if (offerData.checkout_redirect_url) {
        window.location.href = offerData.checkout_redirect_url;
      } else {
        throw new Error("No checkout redirect URL received");
      }
    } catch (error) {
      console.error("Topi checkout error:", error);
      alert("Fehler beim Erstellen der Anfrage: " + error.message);
      submitBtn.disabled = false;
      submitText.style.display = "inline";
      submitLoading.style.display = "none";
    }

    return false;
  };

  // Close modal on overlay click
  document.addEventListener("click", (e) => {
    if (e.target.classList.contains("topi-modal__overlay")) {
      window.closeTopiModal();
    }
  });

  // Close modal on escape key
  document.addEventListener("keydown", (e) => {
    if (e.key === "Escape") {
      window.closeTopiModal();
    }
  });

  /**
   * Set cart items for topi Elements
   * Called from cart pages to enable cart checkout
   * @param {Array} items - Array of cart items from Shopify
   */
  window.setTopiCartItems = (items) => {
    if (!items || !Array.isArray(items) || items.length === 0) {
      return;
    }

    const cartItems = items.map((item) => {
      const cartItem = {
        quantity: item.quantity || 1,
        sellerProductReference: {
          reference: item.sku,
          source: "shopify",
        },
      };

      if (item.price) {
        const grossPrice = item.price;
        const taxRate = 1900;
        const netPrice = Math.round(grossPrice / 1.19);

        cartItem.price = {
          currency: "EUR",
          gross: grossPrice,
          net: netPrice,
          taxRate: taxRate,
        };
      }

      return cartItem;
    });

    topi.cartItems = cartItems;
  };

  /**
   * Fetch current cart from Shopify
   */
  async function fetchShopifyCart() {
    try {
      const response = await fetch('/cart.js');
      if (!response.ok) throw new Error('Failed to fetch cart');
      return await response.json();
    } catch (error) {
      console.error('Error fetching cart:', error);
      return null;
    }
  }

  /**
   * Check eligibility for cart items based on product tags
   * Products must have the "topi" tag to be eligible for rental
   * The eligible variant IDs are passed from Liquid via window.topiEligibleVariantIds
   * @param {Array} items - Cart items from Shopify
   */
  async function checkTopiEligibility(items) {
    const eligible = [];
    const ineligible = [];

    // Get the list of eligible variant IDs from Liquid (products with "topi" tag)
    const eligibleVariantIds = window.topiEligibleVariantIds || [];

    for (let i = 0; i < items.length; i++) {
      const item = items[i];
      const variantId = item.variant_id || item.id;
      const sku = item.sku || item.variant?.sku || String(variantId);

      // Check if this variant is in the eligible list (product has "topi" tag)
      if (eligibleVariantIds.includes(variantId)) {
        eligible.push({
          quantity: item.quantity || 1,
          sellerProductReference: {
            reference: sku,
            source: "shopify",
          },
          price: {
            currency: "EUR",
            gross: item.final_price || item.price,
            net: Math.round((item.final_price || item.price) / 1.19),
            taxRate: 1900,
          },
          title: item.product_title || item.title,
          image: item.featured_image?.url || item.image || "",
          variantId: variantId,
        });
      } else {
        ineligible.push({
          title: item.product_title || item.title,
          reason: "Nicht für Miete verfügbar",
        });
      }
    }

    // Set eligible items on topi for display
    if (eligible.length > 0) {
      const topiItems = eligible.map(item => ({
        quantity: item.quantity,
        sellerProductReference: item.sellerProductReference,
        price: item.price,
        title: item.title,
        image: item.image,
      }));
      topi.cartItems = topiItems;
    }

    return { eligible, ineligible };
  }

  /**
   * Handle cart checkout with Topi
   * Checks eligibility and opens modal with eligible products
   */
  window.handleTopiCartCheckout = async () => {
    const button = document.getElementById("topi-cart-checkout-btn");
    const buttonText = button?.querySelector(".topi-cart-btn__text");
    const buttonLoading = button?.querySelector(".topi-cart-btn__loading");

    // Show loading state
    if (button) {
      button.disabled = true;
      if (buttonText) buttonText.style.display = "none";
      if (buttonLoading) buttonLoading.style.display = "inline-flex";
    }

    try {
      // Fetch current cart
      const cart = await fetchShopifyCart();
      if (!cart || !cart.items || cart.items.length === 0) {
        alert("Ihr Warenkorb ist leer.");
        return;
      }

      // Check eligibility for each item
      const { eligible, ineligible } = await checkTopiEligibility(cart.items);

      // Store results for modal
      eligibleCartItems = eligible;
      ineligibleCartItems = ineligible;

      if (eligible.length === 0) {
        alert("Leider sind keine Produkte in Ihrem Warenkorb für die Miete verfügbar.");
        return;
      }

      // Open modal in cart mode
      window.openTopiModal(true);

    } catch (error) {
      console.error("Topi cart checkout error:", error);
      alert("Fehler beim Prüfen der Mietverfügbarkeit: " + error.message);
    } finally {
      // Reset button state
      if (button) {
        button.disabled = false;
        if (buttonText) buttonText.style.display = "inline-flex";
        if (buttonLoading) buttonLoading.style.display = "none";
      }
    }
  };

  /**
   * Update cart button visibility based on cart contents
   * Button visibility is primarily controlled by Liquid (based on "topi" tag)
   * This function handles dynamic updates after cart changes
   */
  window.updateTopiCartButton = async () => {
    const button = document.getElementById("topi-cart-checkout-btn");
    if (!button) return;

    try {
      const cart = await fetchShopifyCart();
      const eligibleVariantIds = window.topiEligibleVariantIds || [];

      if (cart && cart.items && cart.items.length > 0 && eligibleVariantIds.length > 0) {
        // Check if any cart items are in the eligible list
        const hasEligibleItems = cart.items.some(item =>
          eligibleVariantIds.includes(item.variant_id || item.id)
        );
        button.style.display = hasEligibleItems ? "flex" : "none";
      } else {
        button.style.display = "none";
      }
    } catch (error) {
      console.error("Error updating Topi button:", error);
    }
  };

  // Listen for variant changes on PDP using the theme's pub/sub system
  if (typeof subscribe === "function" && typeof PUB_SUB_EVENTS !== "undefined") {
    subscribe(PUB_SUB_EVENTS.variantChange, (event) => {
      const variant = event.data?.variant;
      if (variant && variant.sku) {
        window.setTopiPdpItem({
          sku: variant.sku,
          price: variant.price,
          quantity: 1,
          title: currentPdpItem?.title || "",
          image: currentPdpItem?.image || "",
        });
      }
    });

    // Listen for cart updates to refresh the Topi button
    subscribe(PUB_SUB_EVENTS.cartUpdate, () => {
      if (typeof window.updateTopiCartButton === "function") {
        window.updateTopiCartButton();
      }
    });
  }

  // Initialize cart button on load
  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", () => {
      window.updateTopiCartButton();
    });
  } else {
    window.updateTopiCartButton();
  }
};

// Wait for the Topi library to be available, then dispatch topi:loaded
if (typeof TopiElements !== "undefined") {
  initTopi();
  document.dispatchEvent(new CustomEvent("topi:loaded"));
} else {
  // If TopiElements isn't ready yet, wait for it via script onload
  const checkTopiElements = setInterval(() => {
    if (typeof TopiElements !== "undefined") {
      clearInterval(checkTopiElements);
      initTopi();
      document.dispatchEvent(new CustomEvent("topi:loaded"));
    }
  }, 50);
}
